<template>
  <w-read-only-text-field
    v-if="label"
    v-bind="$props"
  >
    <template #valueLabel>
      <v-breadcrumbs
        v-if="hasItems"
        class="w-breadcrumbs"
        :items="internalBreadcrumbs"
      />
    </template>
  </w-read-only-text-field>

  <v-breadcrumbs
    v-else
    class="w-breadcrumbs pa-0"
    :items="internalBreadcrumbs"
  />
</template>

<script>
import get from 'lodash/get';

/**
 * @component WorkBreadcrumbs
 * @description Breadcrumb navigation for hierarchical objects (e.g. category tree).
 * Renders a clickable path list or an inline read-only display.
 *
 * @prop {String} [label] - Field label.
 * @prop {*} [value] - Array of breadcrumb objects.
 * @prop {Boolean} [inlineReadOnly=false] - Inline instead of list display.
 * @prop {Array} [errorMessages=[]] - Error messages.
 * @prop {Boolean} [pending=false] - Loading state.
 * @prop {Boolean} [nextToEachOther=false] - Display elements side by side.
 * @prop {Function} [customTo] - Custom navigation target function per entry.
 * @prop {String} [componentName] - Vue Router component name for links.
 * @prop {String} [itemValue='id'] - Path to the value field in each entry.
 * @prop {String} [itemText='data.label'] - Path to the display text in each entry.
 */
export default {
  name: 'WorkBreadcrumbs',
  props: {
    label: String,
    value: null,
    inlineReadOnly: { type: Boolean, default: false },
    errorMessages: { type: Array, default: () => [] },
    pending: { type: Boolean, default: false },
    nextToEachOther: { type: Boolean, default: false },
    customTo: { type: Function, default: undefined },
    componentName: { type: String },
    itemValue: { type: String, default: 'id' },
    itemText: { type: String, default: 'data.label' },
  },
  computed: {
    internalBreadcrumbs() {
      if (this.value === undefined || this.value === null) return [];

      return this.value?.map((item) => ({
        text: get(item, this.itemText),
        to: this.getNavigateTo(item),
      }));
    },
    hasItems() {
      return this.internalBreadcrumbs?.length > 0;
    },
  },
  methods: {
    getNavigateTo(item) {
      if (this.customTo !== undefined) return this.customTo(item);
      else
        return {
          name: this.componentName,
          params: { id: get(item, this.itemValue) },
        };
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~vuetify/src/styles/styles';


::v-deep.w-breadcrumbs {
  padding: 0;

  .v-breadcrumbs__item,
  .v-breadcrumbs__divider {
    @include w-p1;
    color: $input-read-only-color;
    word-break: break-word;
  }
}
</style>